import { getOrCreateWizardInstallationId } from "./wizard-state.js";

const POSTHOG_DEFAULT_HOST = "https://us.i.posthog.com";
// PostHog project ingestion keys are public write-only identifiers. Shipping
// this fallback keeps release telemetry alive when a user's shell does not
// provide OrgX deployment environment variables.
const POSTHOG_DEFAULT_API_KEY = "phc_s4KPgkYEFZgvkMYw4zXG41H5FN6haVwbEWPYHfNjxOc";
const WIZARD_LIB = "@useorgx/wizard";
const TELEMETRY_SCHEMA_VERSION = "2026-07-14";

function isTruthyEnv(value: string | undefined): boolean {
  if (!value) return false;

  switch (value.trim().toLowerCase()) {
    case "1":
    case "true":
    case "yes":
    case "y":
    case "on":
      return true;
    default:
      return false;
  }
}

export function isWizardTelemetryDisabled(): boolean {
  const disabled =
    isTruthyEnv(process.env.ORGX_TELEMETRY_DISABLED) ||
    isTruthyEnv(process.env.OPENCLAW_TELEMETRY_DISABLED) ||
    isTruthyEnv(process.env.POSTHOG_DISABLED);
  if (disabled) return true;

  const explicitEnable = process.env.ORGX_TELEMETRY_ENABLED;
  return explicitEnable !== undefined && !isTruthyEnv(explicitEnable);
}

function resolvePosthogApiKey(): string | null {
  const value =
    process.env.ORGX_POSTHOG_API_KEY ??
    process.env.POSTHOG_API_KEY ??
    process.env.ORGX_POSTHOG_KEY ??
    process.env.POSTHOG_KEY ??
    POSTHOG_DEFAULT_API_KEY;
  const trimmed = value.trim();
  return trimmed || null;
}

function resolvePosthogHost(): string {
  const value =
    process.env.ORGX_POSTHOG_HOST ??
    process.env.POSTHOG_HOST ??
    process.env.ORGX_POSTHOG_API_HOST ??
    process.env.POSTHOG_API_HOST ??
    "";
  const trimmed = value.trim();
  return trimmed || POSTHOG_DEFAULT_HOST;
}

function toPosthogBatchUrl(host: string): string {
  try {
    return new URL("/batch/", host).toString();
  } catch {
    return `${POSTHOG_DEFAULT_HOST}/batch/`;
  }
}

export async function trackWizardTelemetry(
  event: string,
  properties: Record<string, unknown> = {},
  options: { statePath?: string; timestamp?: string } = {},
): Promise<boolean> {
  if (isWizardTelemetryDisabled()) return false;

  const apiKey = resolvePosthogApiKey();
  if (!apiKey) return false;

  const installationId = getOrCreateWizardInstallationId(options.statePath);
  const timestamp = options.timestamp ?? new Date().toISOString();
  const response = await fetch(toPosthogBatchUrl(resolvePosthogHost()), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      api_key: apiKey,
      batch: [
        {
          type: "capture",
          event,
          distinct_id: installationId,
          properties: {
            $lib: WIZARD_LIB,
            source: WIZARD_LIB,
            surface: "cli",
            event_origin: "wizard_cli",
            environment: process.env.NODE_ENV ?? "production",
            telemetry_schema_version: TELEMETRY_SCHEMA_VERSION,
            wizard_installation_id: installationId,
            ...properties,
          },
          timestamp,
        },
      ],
      sent_at: timestamp,
    }),
  }).catch(() => null);

  return response?.ok === true;
}
